import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Sparkles } from "lucide-react";
import { SERVICES } from "../../data/site";
import { Reveal, Kicker } from "./Reveal";
import { useEstimate } from "./EstimateDialog";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Wrench;
  return <C className={className} />;
};

const ACCENT_KEYWORDS = [
  "Wood Slat Walls",
  "Board & Batten",
  "Shiplap",
  "Decorative Wall Panels",
  "Wainscoting",
  "Geometric Accent Walls",
  "Feature Walls",
  "Custom Wall Designs",
  "Modern Interior Finishes",
  "Commercial Feature Walls",
];

const ACCENT_THUMBS = [
  { src: "/projects/accent-3.jpg", alt: "Sage feature wall with wood slat niches and linear fireplace" },
  { src: "/projects/accent-4.webp", alt: "Modern fluted panel feature wall with floating shelves" },
  { src: "/projects/accent-2.webp", alt: "Grey plaster wall with wood slat side panels and LED niches" },
  { src: "/projects/accent-1.webp", alt: "Warm tan accent wall with mounted TV and linear fireplace" },
];

const FeaturedAccentWalls = () => {
  const { open } = useEstimate();
  return (
    <Reveal>
      <div
        data-testid="featured-accent-walls"
        className="relative mb-14 overflow-hidden rounded-3xl border border-ffc-line bg-white shadow-[0_30px_80px_-40px_rgba(13,59,142,0.35)]"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Image side */}
          <div className="relative min-h-[360px] overflow-hidden lg:min-h-[600px]">
            <img
              src="/projects/accent-5.webp"
              alt="Custom marble and wood slat accent wall with LED-backlit TV and linear fireplace by Fast Fix Charlotte"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            <div className="absolute left-6 top-6 inline-flex items-center gap-2 rounded-full bg-ffc-red px-4 py-2 shadow-lg">
              <Sparkles className="h-4 w-4 text-white" />
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white">
                Featured Service
              </span>
            </div>
          </div>

          {/* Content side */}
          <div className="flex flex-col justify-center p-8 sm:p-10 lg:p-14">
            <Kicker>Signature Specialty</Kicker>
            <h3 className="font-display mt-4 text-3xl font-extrabold leading-[1.05] sm:text-4xl lg:text-5xl">
              Accent Wall
              <br />
              <span className="text-ffc-blue">Installation</span>
            </h3>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-ffc-gray">
              Transform any room with a custom accent wall designed to add style, texture, and
              character. We build modern wood slat walls, board and batten walls, shiplap walls,
              geometric feature walls, decorative panel molding, and custom painted accent walls
              for bedrooms, living rooms, offices, entryways, and commercial spaces.
            </p>

            <div className="mt-7 flex flex-wrap gap-2">
              {ACCENT_KEYWORDS.map((k) => (
                <span
                  key={k}
                  data-testid={`accent-tag-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="rounded-full border border-ffc-line bg-ffc-light px-3 py-1.5 text-xs font-semibold text-ffc-blue"
                >
                  {k}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={open}
                data-testid="accent-estimate-btn"
                className="btn-red inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-ffc-red"
              >
                Request Free Estimate
                <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="#gallery"
                data-testid="accent-gallery-link"
                className="inline-flex items-center gap-2 rounded-full border border-ffc-line px-6 py-3.5 text-sm font-semibold text-ffc-ink transition-colors hover:border-ffc-blue hover:text-ffc-blue"
              >
                See Our Work
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Recent projects strip */}
        <div className="grid grid-cols-2 border-t border-ffc-line sm:grid-cols-4">
          {ACCENT_THUMBS.map((t, i) => (
            <div
              key={t.src}
              className={`relative aspect-[4/3] overflow-hidden ${
                i > 0 ? "border-l border-ffc-line" : ""
              } ${i > 1 ? "border-t sm:border-t-0" : ""}`}
              data-testid={`accent-thumb-${i}`}
            >
              <img
                src={t.src}
                alt={t.alt}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-110"
              />
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  );
};

export const Services = () => {
  const { open } = useEstimate();
  return (
    <section id="services" className="bg-white py-24 lg:py-32" data-testid="services">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Reveal><Kicker>What We Do</Kicker></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display mt-4 max-w-3xl text-4xl font-extrabold sm:text-5xl lg:text-6xl">
                Residential &amp; Commercial <span className="text-ffc-blue">Property Solutions</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-ffc-gray">
              Painting, drywall, flooring, kitchen &amp; bathroom renovations,
              remodeling, commercial maintenance and more — delivered with
              premium craftsmanship across Charlotte, NC.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {SERVICES.map((s, i) => (
            <motion.div key={s.name}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="svc-card group relative flex flex-col overflow-hidden rounded-3xl border border-ffc-line bg-white"
              data-testid={`service-card-${i}`}
            >
              {s.bg && (
                <div className="relative h-40 w-full overflow-hidden sm:h-44 md:h-48 lg:h-52">
                  <img
                    src={s.bg}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                  />
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-white/40 to-white"
                  />
                </div>
              )}
              <div className="flex flex-1 flex-col p-7 pt-6 sm:p-8 sm:pt-7">
                <div className="flex items-center justify-between">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ffc-light text-ffc-blue shadow-sm ring-1 ring-black/5 transition-colors group-hover:bg-ffc-blue group-hover:text-white">
                    <Icon name={s.icon} className="h-6 w-6" />
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-ffc-line transition-colors group-hover:text-ffc-red" />
                </div>
                <h3 className="font-display mt-6 text-2xl font-extrabold tracking-tight sm:text-3xl">
                  {s.name}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-ffc-gray">{s.desc}</p>
                {s.items && (
                  <div className="mt-6 flex flex-wrap items-center gap-y-1 text-[13px] leading-relaxed text-ffc-ink/70">
                    {s.items.map((it, idx) => (
                      <span key={it} data-testid={`service-item-${i}-${idx}`} className="inline-flex items-center whitespace-nowrap">
                        {idx > 0 && (
                          <span
                            aria-hidden="true"
                            data-testid={`service-item-dot-${i}-${idx}`}
                            className="mx-2.5 inline-block h-1 w-1 shrink-0 rounded-full bg-ffc-red sm:mx-3 sm:h-1.5 sm:w-1.5"
                          />
                        )}
                        {it}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-col items-center justify-between gap-5 rounded-2xl bg-ffc-ink p-8 sm:flex-row lg:p-10">
            <p className="font-display text-2xl font-bold text-white sm:text-3xl">
              Don&apos;t see it listed? We probably do that too.
            </p>
            <button onClick={open} data-testid="services-estimate-btn"
              className="btn-red shrink-0 rounded-full px-7 py-4 text-base font-semibold hover:bg-ffc-red">
              Request a Free Estimate
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
